<template>
  <div class="flex-center position-ref full-height">
    <div class="content">
      <Loading :loading="loading"></Loading>
      <img src="/assets/logos/boostifyedu.png" alt="BoostifyEdu">

      <h3 v-if="status">{{ status }}</h3>
      <h3 v-if="!$route.query.sso">SSO parametresi bulunamadı. (?sso= bekleniyor)</h3>
      <SsoError v-if="error" :message="error"></SsoError>

      <!-- debug modu: adımlar ekrana basılır, yönlendirme kullanıcı onaylayana kadar bekletilir -->
      <div v-if="debug" class="be-debug">
        <h4>DEBUG MODE — flow is paused, nothing has been sent to BoostifyEdu yet.</h4>

        <div v-for="(step, index) in steps" :key="index" class="be-debug-step">
          <div :class="['be-debug-title', step.ok ? 'is-ok' : 'is-fail']">
            [{{ index + 1 }}] {{ step.title }} {{ step.ok ? '✔' : '✖' }}
          </div>
          <pre v-if="step.data">{{ step.data }}</pre>
        </div>

        <button v-if="redirectUrl" class="be-debug-button" @click="redirectNow">
          Go BoostifyEdu →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import SsoError from "@/components/SsoError";
import axios from "axios";
import CryptoJS from "crypto-js";

export default {
  name: "BoostifyEdu",
  components: {
    Loading,
    SsoError
  },
  data() {
    return {
      loading: false,
      // service.ueslms.com'a gelen `sso` parametresini çözmek için kullanılan AES anahtarı.
      // Metodbox'ın HLL / UESPortals girişlerinde kullandığı anahtarla aynıdır; böylece
      // Metodbox tarafında yalnızca hedef adres (#/boostifyedu) değişir, şifreleme aynı kalır.
      secretKey: 'T6VCnkxXEx8ixtHxpteZnbm0YF9KwvLU0iBTOf1vJz1Y3fzZ',
      // BoostifyEdu'nun OCL giriş adresi. Alan adı kesin; `/sso/ues` yolu BoostifyEdu
      // ekibiyle teyit edilecek — farklıysa yalnızca bu satır değişir.
      boostifyEduUrl: 'https://www.boostifyedu.com/sso/ues',
      apiUrl: 'https://www.ueslms.com/api/v2/boostifyedu',
      // apiUrl: 'https://ueslms.test/api/v2/boostifyedu',
      userData: null,    // Metodbox'tan gelen çözülmüş payload
      userLmsData: null, // ueslms'ten dönen kullanıcı
      token: null,       // BoostifyEdu'ya iletilecek şifreli payload (ueslms üretir, bkz. goBoostifyEdu)
      redirectUrl: null,
      debug: false,
      steps: [],
      status: null, // kullanıcının ekranda gördüğü ilerleme metni
      error: null,  // beklenen bir ret sebebi (ör. lisans/yetki reddi) — SsoError ile gösterilir
    }
  },
  beforeMount() {
    const query = this.$route.query;

    // debug hem query string'ten hem de şifreli payload'ın içinden gelebilir
    this.debug = this.isDebug(query.debug);

    if (!query.sso) {
      return;
    }

    this.loading = true;

    try {
      const decode = decodeURIComponent(atob(query.sso));
      const bytes = CryptoJS.AES.decrypt(decode, this.secretKey).toString(CryptoJS.enc.Utf8);
      this.userData = JSON.parse(bytes);
    } catch (e) {
      this.loading = false;
      this.debug = true; // çözülemeyen payload'ı her durumda göster
      this.setStatus('Giriş bilgileri çözülemedi.', 'Login data could not be decrypted.');
      this.log('sso payload could not be decrypted', String(e), false);
      return;
    }

    if (this.isDebug(this.userData.debug)) {
      this.debug = true;
    }

    this.log('received from your platform (decrypted sso payload)', this.userData);
    this.setStatus('Kullanıcı bilgileriniz kontrol ediliyor, lütfen bekleyiniz.', 'Checking the user on UES…');
    this.ueslmsCheckUser();
  },
  methods: {
    isDebug(val) {
      return val === true || val === 'true' || val === 1 || val === '1';
    },

    // Normal akışta ekranı öğrenci/öğretmen görür (Türkçe), debug modunda ise
    // entegrasyonu test eden geliştirici görür (İngilizce).
    setStatus(tr, en) {
      this.status = this.debug ? en : tr;
    },

    log(title, data, ok = true) {
      this.steps.push({
        title: title,
        data: typeof data === 'string' ? data : JSON.stringify(data, null, 2),
        ok: ok,
      });
    },

    // Hata durumunda akış durur; debug kapalıysa bile panel açılır ki sebep görülebilsin.
    // Karşı tarafa yalnızca HTTP durumu gösterilir, UES uç noktası/gövdesi konsolda kalır.
    handleError(error) {
      this.loading = false;
      this.debug = true;
      console.log('ERR', error);
      const status = error.response ? error.response.status : '-';
      this.setStatus('İşlem tamamlanamadı. Lütfen okulunuzla iletişime geçiniz.', 'The process could not be completed.');
      this.log('UES could not resolve the user', 'HTTP ' + status + ' — please contact UES.', false);
    },

    // handleError()'dan farklı olarak: burada UES'ten beklenen, açıklanmış bir ret var
    // (ör. lisans/yetki reddi) — HTTP hatası değil, 200 + success:false + message. Gerçek
    // mesaj kullanıcıya SsoError ile gösterilir, debug panelde de görülür.
    fail(message) {
      this.loading = false;
      this.debug = true;
      this.error = message;
      this.setStatus(message, message);
      this.log('UES denied the request', message, false);
    },

    ueslmsCheckUser() {
      // UES uç noktaları/ham yanıtları karşı tarafı ilgilendirmediği için panele yazılmaz;
      // yalnızca sorgunun sonucu (kullanıcı bulundu / oluşturuldu) bildirilir.
      axios.post(this.apiUrl + '/user/check', { user: this.userData, debug: this.debug })
          .then(response => {
            if (response.data.success) {
              this.userLmsData = response.data.user;
              this.log('UES account: existing user matched');
              this.goBoostifyEdu(response.data.token);
            } else if (response.data.message) {
              // success:false + message → örn. lisans/yetki reddi. Kullanıcı henüz yok
              // diye create'e düşmek yerine reddi doğrudan gösteriyoruz, çünkü create de
              // aynı kontrolden geçip aynı sebeple reddedilecek.
              this.fail(response.data.message);
            } else {
              // kullanıcı UES'te yok, oluşturuluyor
              this.setStatus('Kullanıcı kaydınız oluşturuluyor, lütfen bekleyiniz.', 'Creating the user on UES…');
              this.ueslmsCreate();
            }
          })
          .catch(error => {
            this.handleError(error);
          })
    },

    ueslmsCreate() {
      axios.post(this.apiUrl + '/user/create', { ...this.userData, debug: this.debug })
          .then(response => {
            if (response.data.success) {
              this.userLmsData = response.data.user;
              this.log('UES account: new user created');
              this.goBoostifyEdu(response.data.token);
            } else {
              this.fail(response.data.message || 'Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
            }
          })
          .catch(error => {
            this.handleError(error);
          })
    },

    // Token (şifreli kullanıcı payload'ı) ueslms tarafından üretilir: BoostifyEduController::ssoToken().
    // Eskiden burada tarayıcıda CryptoJS ile üretiliyordu ve anahtar JS bundle'ında açık duruyordu.
    // Payload alanları değişmedi; sunucu ayrıca `timestamp` (Unix saniye) ekler.
    goBoostifyEdu(token) {
      if (!token) {
        // Sunucuda BOOSTIFYEDU_SSO_SECRET tanımlı değilse token gelmez (ueslms log'a hata yazar)
        this.fail('Giriş anahtarı oluşturulamadı. Lütfen okulunuzla iletişime geçiniz.');
        return;
      }

      this.token = token;
      this.redirectUrl = this.boostifyEduUrl ? `${this.boostifyEduUrl}?token=${this.token}` : null;

      if (this.debug) {
        this.loading = false;
        this.log('to be sent to BoostifyEdu (UES user — the token payload is built from this on the server)', this.userLmsData);
        this.log('token (this is what BoostifyEdu receives)', this.token);
        this.status = 'Ready. Press the button below to send the payload to BoostifyEdu.';
        this.log('redirect url', this.redirectUrl);

        return; // yönlendirme "Go BoostifyEdu" butonuna bırakılır
      }

      this.redirectNow();
    },

    redirectNow() {
      // Yönlendirme sonrası giriş BoostifyEdu tarafında tamamlanır; kullanıcı sekmede
      // bir süre bekleyebileceği için ekranda son durum metni bırakılır.
      this.setStatus(
          'BoostifyEdu\'ya yönlendiriliyorsunuz. Girişiniz, bilgileriniz BoostifyEdu\'ya ulaştığında tamamlanacaktır.',
          'Redirecting to BoostifyEdu. The login will be completed once BoostifyEdu receives the details.'
      );
      window.location.href = this.redirectUrl;
    },
  }
}
</script>

<style scoped>
.be-debug {
  max-width: 900px;
  margin: 20px auto;
  text-align: left;
  font-family: monospace;
  font-size: 13px;
}

.be-debug-step {
  margin-bottom: 12px;
}

.be-debug-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.be-debug-title.is-ok {
  color: #1b7f3b;
}

.be-debug-title.is-fail {
  color: #c0261a;
}

.be-debug pre {
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 10px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.be-debug-button {
  margin-top: 10px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
}
</style>
